import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Global guard: listens for `trip-access-invalid` events (fired by api.js
 * interceptor on any 403 invalid_trip_access) and redirects to the trip's
 * password gate when the current URL is a trip-scoped route.
 *
 * Mounted once inside BrowserRouter. Protects ALL trip-scoped pages from
 * rendering blank when localStorage is empty / evicted / cleared.
 *
 * TripDetail has its own inline handling (shows gate directly), so this
 * redirect is a no-op when already on `/trip/{code}` (React Router
 * replace-navigation to the same path is harmless).
 */
export default function TripAccessRedirectGuard() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handler = (e) => {
      const tripCode = e.detail?.code;
      if (!tripCode) return;

      // Only redirect if we're currently on a trip-scoped route for this code
      const path = location.pathname;
      if (!path.startsWith(`/trip/${tripCode}`)) return;

      // Already on the gate page — TripDetail handles this inline
      if (path === `/trip/${tripCode}`) return;

      // Redirect to TripDetail which will show the password gate
      navigate(`/trip/${tripCode}`, { replace: true });
    };

    window.addEventListener('trip-access-invalid', handler);
    return () => window.removeEventListener('trip-access-invalid', handler);
  }, [navigate, location.pathname]);

  return null;
}
